import { Button, Input, Modal, Segmented, message } from 'antd';
import i18next from 'i18next';
import React, { useEffect, useState } from 'react';
import { archiveLogo, boardDetail, LibraryAuthError, LibraryBoard, LibraryBoardDetail, LibraryLogo, listBoards, listLogos, renameLogo } from './library';
import Marmotte from './Marmotte';
import { TutoLink } from './Tutos';

/* « Ma bibliothèque » (Daniel 09.10 / feu vert 15.10.2026, dossier écrans 3 et 4) : vignettes des logos FINIS, un clic → sur la planche sans
   retraitement ; renommer ; archiver (jamais supprimer : « DTF Swiss le garde ») ; onglet « Mes planches » → recommander au tarif du jour.
   Aucune adresse de fichier dans la page : seulement des URL signées 5 min sur des vignettes. */
interface IProps {
	open: boolean;
	onClose: () => void;
	onAddLogo: (logo: LibraryLogo) => Promise<void>;
	onReorder: (board: LibraryBoardDetail) => Promise<void>;
	refreshKey?: number;
}

const fmtDate = (iso: string) => {
	try {
		return new Date(iso).toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
	} catch {
		return '';
	}
};

const LibraryModal: React.FC<IProps> = ({ open, onClose, onAddLogo, onReorder, refreshKey = 0 }) => {
	const t = (k: string, o?: Record<string, unknown>) => i18next.t(`dtf.${k}`, o) as string;
	const [tab, setTab] = useState<'logos' | 'planches'>('logos');
	const [logos, setLogos] = useState<LibraryLogo[] | null>(null);
	const [boards, setBoards] = useState<LibraryBoard[] | null>(null);
	const [q, setQ] = useState('');
	const [err, setErr] = useState<string | null>(null);
	const [busy, setBusy] = useState<string | null>(null);
	const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);
	const [confirmArchive, setConfirmArchive] = useState<LibraryLogo | null>(null);

	const load = async () => {
		setErr(null);
		try {
			// Daniel 22.10 : les deux listes d'un coup — l'onglet « Mes planches » porte son compteur dès l'ouverture (sinon personne ne le voit)
			const [l, b] = await Promise.all([listLogos(), listBoards()]);
			setLogos(l);
			setBoards(b);
		} catch (e) {
			setErr(e instanceof LibraryAuthError ? t('biblio-connexion') : (e as Error).message || t('biblio-erreur'));
		}
	};
	useEffect(() => {
		if (open) load();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, refreshKey]);

	const filtered = (logos || []).filter(l => !q.trim() || l.name.toLowerCase().includes(q.trim().toLowerCase()));

	const add = async (l: LibraryLogo) => {
		setBusy(l.id);
		try {
			await onAddLogo(l);
			message.success(t('biblio-ajoute', { name: l.name }), 4);
		} catch (e) {
			message.error((e as Error).message || t('biblio-erreur'), 6);
		} finally {
			setBusy(null);
		}
	};
	const doRename = async () => {
		if (!renaming) return;
		const name = renaming.name.trim();
		if (!name) return;
		setBusy(renaming.id);
		try {
			await renameLogo(renaming.id, name);
			setLogos(prev => (prev || []).map(l => (l.id === renaming.id ? { ...l, name } : l)));
			setRenaming(null);
		} catch (e) {
			message.error((e as Error).message || t('biblio-erreur'), 6);
		} finally {
			setBusy(null);
		}
	};
	const doArchive = async () => {
		if (!confirmArchive) return;
		const l = confirmArchive;
		setBusy(l.id);
		try {
			await archiveLogo(l.id);
			setLogos(prev => (prev || []).filter(x => x.id !== l.id));
			setConfirmArchive(null);
			message.info(t('biblio-archive-ok', { name: l.name }), 5);
		} catch (e) {
			message.error((e as Error).message || t('biblio-erreur'), 6);
		} finally {
			setBusy(null);
		}
	};
	const reorder = async (b: LibraryBoard) => {
		setBusy(b.board_version_id);
		try {
			const d = await boardDetail(b.board_version_id);
			await onReorder(d);
			onClose();
		} catch (e) {
			message.error((e as Error).message || t('biblio-erreur'), 6);
		} finally {
			setBusy(null);
		}
	};

	const chips = (l: LibraryLogo) => {
		const c: string[] = [];
		if (l.steps.includes('fond')) c.push('✂️ ' + t('biblio-chip-fond'));
		if (l.vectorized || l.steps.includes('vecto')) c.push('✒️ ' + t('biblio-chip-vecto'));
		if (l.steps.includes('nettete')) c.push('✨ ' + t('biblio-chip-nettete'));
		if (l.steps.includes('traits')) c.push('🖌 ' + t('biblio-chip-traits'));
		return c;
	};

	return (
		<Modal open={open} onCancel={onClose} footer={null} centered width={980} className="dtf-lib-modal" title={<span className="dtf-lib-title">📚 {t('biblio-titre')}</span>} destroyOnClose>
			<div className="dtf-lib-head">
				<Segmented
					size="large"
					value={tab}
					onChange={v => setTab(v as 'logos' | 'planches')}
					options={[
						{ label: t('biblio-onglet-logos', { n: logos ? logos.length : '…' }), value: 'logos' },
						{ label: t('biblio-onglet-planches', { n: boards === null ? '…' : boards.length }), value: 'planches' },
					]}
				/>
				{tab === 'logos' && <Input.Search allowClear size="large" className="dtf-lib-search" placeholder={t('biblio-recherche')} value={q} onChange={e => setQ(e.target.value)} />}
			</div>
			{err && (
				<div className="dtf-lib-error">
					{err} <Button size="small" onClick={load}>{t('biblio-reessayer')}</Button>
				</div>
			)}
			{tab === 'logos' && !err && (
				<div className="dtf-lib-body">
					{logos === null ? (
						<p className="dtf-prep-hint">{t('biblio-chargement')}</p>
					) : !logos.length ? (
						<Marmotte pose="point" tone="ok" size={64} className="dtf-lib-empty" title={t('biblio-vide-t')}>
							{t('biblio-vide')}
							<TutoLink n={6} />
						</Marmotte>
					) : !filtered.length ? (
						<p className="dtf-prep-hint">{t('biblio-aucun-resultat')}</p>
					) : (
						<div className="dtf-lib-grid">
							{filtered.map(l => (
								<div className="dtf-lib-card" key={l.id} data-testid="lib-card">
									<div className="dtf-lib-thumb checker">{l.thumb_url ? <img src={l.thumb_url} alt="" loading="lazy" /> : <span className="dtf-lib-nothumb">—</span>}</div>
									{renaming?.id === l.id ? (
										<div className="dtf-lib-rename">
											<Input size="small" maxLength={80} value={renaming.name} autoFocus onChange={e => setRenaming({ id: l.id, name: e.target.value })} onPressEnter={doRename} />
											<Button size="small" type="primary" loading={busy === l.id} onClick={doRename}>{t('biblio-ok')}</Button>
											<Button size="small" onClick={() => setRenaming(null)}>{t('biblio-annuler')}</Button>
										</div>
									) : (
										<div className="dtf-lib-name">{l.name}</div>
									)}
									<div className="dtf-lib-meta">
										{fmtDate(l.created_at)}
										{l.width && l.height && !l.vectorized ? ` · ${l.width}×${l.height} px` : ''}
										{l.vectorized ? ` · ${t('badge-vectoriel')}` : ''}
									</div>
									<div className="dtf-lib-chips">{chips(l).map(c => <span key={c} className="dtf-lib-chip">{c}</span>)}</div>
									<div className="dtf-lib-actions">
										<Button type="primary" size="large" block className="dtf-lib-add" loading={busy === l.id} disabled={!l.version_id} onClick={() => add(l)}>
											＋ {t('biblio-ajouter')}
										</Button>
										<div className="dtf-lib-actions-sec">
											<Button size="small" onClick={() => setRenaming({ id: l.id, name: l.name })}>✏️ {t('biblio-renommer')}</Button>
											<Button size="small" danger onClick={() => setConfirmArchive(l)}>🗄 {t('biblio-archiver')}</Button>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
					<p className="dtf-prep-hint dtf-prep-hint-small dtf-lib-foot">{t('biblio-pied')}</p>
				</div>
			)}
			{tab === 'planches' && !err && (
				<div className="dtf-lib-body">
					{boards === null ? (
						<p className="dtf-prep-hint">{t('biblio-chargement')}</p>
					) : !boards.length ? (
						<Marmotte pose="point" tone="ok" size={64} className="dtf-lib-empty" title={t('biblio-planches-vide-t')}>
							{t('biblio-planches-vide')}
						</Marmotte>
					) : (
						<div className="dtf-lib-boards">
							<p className="dtf-lib-intro">{t('biblio-planches-intro')}</p>
							{boards.map(b => (
								<div className="dtf-lib-board" key={b.board_version_id} data-testid="lib-board">
									<div className="dtf-lib-board-prev checker">{b.preview_url ? <img src={b.preview_url} alt="" loading="lazy" /> : <span className="dtf-lib-nothumb">—</span>}</div>
									<div className="dtf-lib-board-info">
										{/* Daniel 22.10 : le nom donné dans l'éditeur en premier (sinon « Préparée le … » partout) */}
										<div className="dtf-lib-name" data-testid="lib-board-name">{(b.title || '').trim() || t('biblio-planche-sans-nom', { d: fmtDate(b.created_at) })}</div>
										<div className="dtf-lib-meta">
											{b.order ? t('biblio-planche-commande', { d: fmtDate(b.order.order_created_at || b.order.at) }) : t('biblio-planche-sans-commande', { d: fmtDate(b.created_at) })}
										</div>
										<div className="dtf-lib-meta">
											{Math.round(b.width_mm / 10)}×{Math.round(b.height_mm / 10)} cm · ×{b.qty} · {b.glue === 'anti' ? t('colle-anti') : t('colle-base')} · {b.items.length} {t('biblio-elements')}
										</div>
										<div className="dtf-lib-board-items">{b.items.slice(0, 6).map((i, k) => <span key={k} className="dtf-lib-chip">{i.label || t('biblio-element')} · {Math.round(i.width_mm / 10 * 10) / 10} cm</span>)}{b.items.length > 6 ? <span className="dtf-lib-chip">…</span> : null}</div>
										<Button type="primary" size="large" className="dtf-lib-reorder" loading={busy === b.board_version_id} onClick={() => reorder(b)}>
											🔁 {t('biblio-recommander')}
										</Button>
									</div>
								</div>
							))}
						</div>
					)}
					<p className="dtf-prep-hint dtf-prep-hint-small dtf-lib-foot">{t('biblio-planches-pied')}</p>
				</div>
			)}
			<Modal open={!!confirmArchive} onCancel={() => setConfirmArchive(null)} onOk={doArchive} okText={t('biblio-archiver')} cancelText={t('biblio-annuler')} okButtonProps={{ danger: true, loading: !!busy }} title={t('biblio-archiver-t', { name: confirmArchive?.name || '' })} centered>
				<p>{t('biblio-archiver-aide')}</p>
			</Modal>
		</Modal>
	);
};

export default LibraryModal;
